import clsx from "clsx";
import { useFormik } from "formik";
import moment from "moment/moment";
import { Chip } from "primereact/chip";
import { Tag } from "primereact/tag";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { RxCross2 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import Chart from "../../components/Chart";
import DashboardStats from "../../components/DashboardStats";
import DataTableWrapper from "../../components/DataTableWrapper";
import DatePicker from "../../components/DatePicker";
import DotBall from "../../components/DotBall";
import Dropdown from "../../components/Dropdown";
import Loader from "../../components/Loader";
import PaddingContainer from "../../components/PaddingContainer";
import PageHeading from "../../components/PageHeading";
import Popover from "../../components/Popovers";
import StatusIndicator from "../../components/StatusIndicator";
import TableValue from "../../components/TableValue";
import TabsContainer from "../../components/TabsContainer";
import AreYouSureWantToCompleteModal from "../../modals/AreYouSureWantToCompleted";
import {
  CalendarIcon,
  CancelAndRefundIcon,
  DownloadPDFIcon,
  InvoiceIcon,
  ReceivePaymentIcon,
  RefreshIcon,
  RefundCancelIcon,
  ViewDetailIcon,
} from "../../constant";
import useDebounce from "../../CustomHooks/useDebounce";
import { paidOptions, statsPeriod, statusOptions } from "../../data";
import {
  formatNumberWithCommas,
  formattedDate,
  formatText,
} from "../../helpers";
import AreYouSureModal from "../../modals/AreYouSureModal";
import AddAndRefundModal from "../../modals/Bookings/AddAndRefund";
import AddOrEditBookingModal from "../../modals/Bookings/AddOrEditBookingModal";
import PaymentReceivedModal from "../../modals/Bookings/PaymentReceived";
import RescheduleBookingModal from "../../modals/Bookings/ReschduleBooking";
import { exportDetailsToPDF } from "../../pdfs/bookings/bookingDetailPdf";
import {
  clearSelectDate,
  setLastVisitedPage,
  setPagination,
  setSortState,
  updateFilter,
} from "../../store/bookingFilterSlice";
import { setPageLink, setPageTitle } from "../../store/pageSlice";
import useApi from "../../useApi";
import classes from "./Dashboard.module.css";
import { CompletedIcon } from "../../constant";
// Add sorting fields enum at the top
const BookingSortFields = {
  DateTime: 1,
  CourtName: 3,
  Amount: 4,
  BookingStatus: 5,
  BookingReference: 6,
};

const formattedDateHandler = ({
  bookingDate,
  bookingStartTime,
  bookingEndTime,
}) => {
  // Convert to time range (1-hour slot)
  const startTime = moment(bookingStartTime, "HH:mm:ss").format("hh:mm A"); // "04:00 PM"
  const endTime = moment(bookingEndTime, "HH:mm:ss").format("hh:mm A");
  return (
    <TableValue
      top={formattedDate(bookingDate)}
      down={`${startTime} - ${endTime}`}
    />
  );
};

const RoleBodyTemplate = ({ role }) => <Chip label={role} />;
RoleBodyTemplate.propTypes = {
  role: PropTypes.string.isRequired,
};

const RoleStatusBodyTemplate = ({ roleStatus }) => (
  <Tag
    value={roleStatus}
    severity={roleStatus === "Active" ? "success" : "warning"}
  />
);

RoleStatusBodyTemplate.propTypes = {
  roleStatus: PropTypes.string.isRequired,
};

const RoleTypeBodyTemplate = ({ roleType }) => <Chip label={roleType} />;

RoleTypeBodyTemplate.propTypes = {
  roleType: PropTypes.string.isRequired,
};

const statusMenuItems = (rowData, onCancel, onConfirm) => [
  {
    icon: <DotBall bookingStatus={1} />,
    label: "Confirm",
    command: () => {
      onConfirm(rowData);
    },
  },
  {
    icon: <DotBall bookingStatus={2} />,
    label: "Cancel",
    command: () => {
      onCancel(rowData);
    },
  },
];

const statusMenuItemsForComplete = (rowData, onCancel, onConfirm) => [
  {
    icon: <DotBall bookingStatus={4} />,
    label: "Completed",
    command: () => {
      onConfirm(rowData);
    },
  },
  {
    icon: <DotBall bookingStatus={2} />,
    label: "Cancel",
    command: () => {
      onCancel(rowData);
    },
  },
];
const columns = (
  items,
  setSelectedItem,
  navigate,
  onAddAndRefund,
  onReceivePayment,
  onBookingCancel,
  onBookingConfirm,
  setRefundCancelModalType,
  onRescheduleBooking,
  onCompleted
) => [
  {
    field: "bookingNo",
    header: "Reference ID",
    sortable: true,
    sortField: BookingSortFields.BookingReference,
    body: (rowData) => {
      return (
        <TableValue top={rowData?.bookingNo?.split("-")?.slice(1).join("-")} />
      );
    },
  },
  {
    field: "bookingDate",
    header: "Date & Time",
    sortable: true,
    sortField: BookingSortFields.DateTime,
    body: (rowData) => {
      return formattedDateHandler({
        bookingDate: rowData?.bookingDate,
        bookingStartTime: rowData?.bookingStartTime,
        bookingEndTime: rowData?.bookingEndTime,
      });
    },
  },
  {
    field: "name",
    header: "Name",
    body: (rowData) => {
      console.log("Row Data", rowData);
      return (
        <>
          <TableValue
            top={
              rowData.bookingPlayers?.find((data) => data?.isPrimary)
                ?.playerFullName
            }
            down={`0${rowData.bookingPlayers
              ?.find((data) => data?.isPrimary)
              ?.playerCellNumber?.slice(3)}`.replace(/(\d{4})(\d{7})/, "$1 $2")}
          />
          {rowData?.corporateName && (
            <p className={classes.corporateName}>{rowData?.corporateName}</p>
          )}
        </>
      );
    },
  },
  {
    field: "courtName",
    header: "Court Name",
    sortable: true,
    sortField: BookingSortFields.CourtName,
    body: (rowData) => (
      <TableValue top={rowData?.courtName} down={rowData?.location} />
    ),
  },
  {
    field: "bookingNetAmount",
    header: "Amount",
    sortable: true,
    sortField: BookingSortFields.Amount,
    body: (rowData) => (
      <TableValue
        top={formatNumberWithCommas(rowData?.bookingNetAmount)}
        down={`Collected: ${formatNumberWithCommas(
          rowData?.bookingCollectedAmount
        )}`}
      />
    ),
  },
  {
    field: "status",
    header: "Booking Status",
    sortable: true,
    sortField: BookingSortFields.BookingStatus,
    body: (rowData) => {
      let menuItems = [];
      if (rowData?.bookingStatusId === 3) {
        menuItems = statusMenuItems(
          rowData,
          () => onBookingCancel(rowData),
          () => onBookingConfirm(rowData)
        );
      }else if(rowData?.bookingStatusId === 1){
        menuItems =  statusMenuItemsForComplete(
          rowData,
          ()=>onBookingCancel(rowData),
          ()=>onCompleted(rowData)
        )
      }

      return (
        <StatusIndicator
          onConfirm={() => {
            onCompleted(rowData);
          }}
          bookingStatus={rowData?.bookingStatusId}
          menuItems={menuItems}
        />
      );
    },
  },
  {
    field: "actions",
    header: "Actions",
    body: (rowData) => {
      const currentDateTime = moment();
      const bookingDateTime = moment(
        `${rowData?.bookingDate} ${rowData?.bookingStartTime}`
      );
      const isFutureBooking = bookingDateTime.isAfter(currentDateTime);
      const isPaymentComplete =
        rowData?.bookingCollectedAmount === rowData?.bookingNetAmount;

      const items = [
        {
          label: "View Detail",
          icon: <ViewDetailIcon className={classes.menuIcon} />,
          command: () => {
            navigate(`/bookings/${rowData.bookingId}`);
          },
        },
        !isPaymentComplete && {
          label: "Receive Payment",
          icon: <ReceivePaymentIcon className={classes.menuIcon} />,
          command: () => {
            if (
              rowData?.bookingStatusId !== 2 &&
              rowData?.bookingStatusId !== 3
            ) {
              onReceivePayment(rowData);
            }
          },
          disabled: [2, 3].includes(rowData?.bookingStatusId),
        },

         rowData?.bookingStatusId === 1 && {
          label: "Complete Booking",
          icon: <CompletedIcon  className={classes.completedIcon}/>,
          command: () => {
            if (rowData?.bookingStatusId == 1) {
              onCompleted(rowData);
            }
          },
        },
        rowData?.bookingStatusId === 1 &&
          isFutureBooking && {
            label: "Reschedule",
            icon: <CalendarIcon className={classes.menuIcon} />,
            command: () => {
              if (
                rowData?.bookingStatusId !== 2 &&
                rowData?.bookingStatusId !== 3
              ) {
                onRescheduleBooking(rowData);
              }
            },
            disabled: [2, 3].includes(rowData?.bookingStatusId),
          },
        rowData?.bookingStatusId === 1 && {
          label: "Refund",
          icon: <RefundCancelIcon className={classes.menuIcon} />,
          command: () => {
            if (
              rowData?.bookingStatusId !== 2 &&
              rowData?.bookingStatusId !== 3
            ) {
              setRefundCancelModalType("refund");
              onAddAndRefund(rowData);
            }
          },
          disabled: [2, 3].includes(rowData?.bookingStatusId),
        },
        rowData?.bookingStatusId === 1 && {
          label: "Refund/Cancel",
          icon: <CancelAndRefundIcon className={classes.menuIcon} />,
          command: () => {
            if (
              rowData?.bookingStatusId !== 2 &&
              rowData?.bookingStatusId !== 3
            ) {
              setRefundCancelModalType("");
              onAddAndRefund(rowData);
            }
          },
          disabled: [2, 3].includes(rowData?.bookingStatusId),
        },
        {
          label: "Download PDF",
          icon: <DownloadPDFIcon className={classes.menuIcon} />,
          command: () => {
            if (![2, 3].includes(rowData?.bookingStatusId)) {
              exportDetailsToPDF(rowData);
            }
          },
          disabled: [2, 3].includes(rowData?.bookingStatusId),
        },
      ]?.filter(Boolean);

      return <Popover items={items} />;
    },
  },
];
const initialValues = {
  status: 3,
  selectDate: [
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ],
};

export default function Booking() {
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dataTableRef = useRef(null); // Add ref for DataTable
  // Get booking filters from Redux
  const bookingFilter = useSelector((state) => state?.bookingFilter);
  const { allPreLoadedApis: preloadedApis } = useSelector((state) => state);
  const [showAddOrEditBookingModal, setShowAddOrEditBookingModal] =
    useState(false);
  const [showRefundAndCancelModal, setShowRefundAndCancelModal] =
    useState(false);
  const [showPaymentReceiveModal, setShowPaymentReceiveModal] = useState(false);
  const [showCancelBookingModal, setShowCancelBookingModal] = useState(false);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [period, setPeriod] = useState(3);
  // Use filter values from Redux
  const [first, setFirst] = useState(bookingFilter.first);
  const [rows, setRows] = useState(bookingFilter.rows);
  const [search, setSearch] = useState(bookingFilter.search);
  const { sortState } = useSelector((state) => state.bookingFilter);
  const [dashboardStats, setDashboardStats] = useState(null);
  const debouncedValue = useDebounce(search, 500);
  const [refundCancelModalType, setRefundCancelModalType] = useState("");

  const [showAreYouSureCompletedModal, setShowAreYouSureCompletedModal] =
    useState(false);
  // Set up API hooks
  const {
    getBookingsAdmin,
    addBooking,
    refundAndCancel,
    paymentReceive,
    confirmBooking,
    getDashboardStats,
    rescheduleBooking,
    bookingCompleted,
  } = useApi();

  // Set up formik with redux values
  const formik = useFormik({
    initialValues: {
      status: bookingFilter.status || 3,
      paid: bookingFilter.paid,
      court: bookingFilter.court,
      locations: bookingFilter.locations,
      selectDate: bookingFilter.selectDate,
    },
    onSubmit: () => {},
  });

  const { values, setFieldValue } = formik;

  // Handle filter change and update Redux
  const handleChange = (filterName) => (value) => {
    setFieldValue(filterName, value);
    dispatch(updateFilter({ filterName, value }));
  };

  const getDashboardStatsApi = async (params, allowLoading = true) => {
    if (allowLoading) setLoading(true);
    try {
      const result = await getDashboardStats(params);
      if (result?.data?.headers?.error === 200) {
        setDashboardStats(result?.data?.body);
      }
    } catch (error) {
      toast.error(new Error(error)?.message);
    }
    if (allowLoading) setLoading(false);
  };

  // Set up options for dropdowns
  const options = {
    status: statusOptions,
    paid: paidOptions,
    court: preloadedApis?.courts?.map((data) => ({
      label: data?.courtName,
      value: data?.courtId,
    })),
    locations: preloadedApis?.locations?.map((data) => ({
      label: data?.locationName,
      value: data?.locationId,
    })),
  };

  // Update first in local state and Redux
  const updateFirst = (newFirst) => {
    setFirst(newFirst);
    dispatch(
      setPagination({
        page: Math.floor(newFirst / rows) + 1,
        first: newFirst,
      })
    );
  };
  const onRescheduleBooking = (data) => {
    setSelectedItem(data);
    setShowRescheduleModal(true);
  };

  // Handle page change
  const handlePageChange = (e) => {
    setRows(e.rows);
    updateFirst(e.first);
    getBookings({
      ...getBookingParams(),
      pageNumber: e.page + 1,
      pageSize: e.rows,
    });
  };
  const rescheduleBookingApi = async (data) => {
    setLoading(true);
    try {
      const result = await rescheduleBooking(data);
      if (result?.data?.headers?.error === 200) {
        toast.success(result?.data?.headers?.message);
        setShowRescheduleModal(false);
       await getBookings(getBookingParams());
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error("Failed to reschedule booking");
    }
    setLoading(false);
  };
  // Handle search chang

  // Get parameters for booking API calls
  const getBookingParams = () => {
    let startDate = values?.selectDate?.[0]?.startDate;
    let endDate = values?.selectDate?.[0]?.endDate;
    let sortStateOrder = sortState?.order || -1;
    return {
      search: search,
      pageNumber: Math.floor(first / rows) + 1,
      pageSize: rows,
      bookingDateStart: startDate
        ? moment(startDate)?.format("YYYY-MM-DD")
        : null,
      bookingDateEnd: endDate ? moment(endDate)?.format("YYYY-MM-DD") : null,
      locationId: values?.locations || null,
      courtId: values?.court || null,
      bookingStatusId: values?.status || null,
      isPaid: values?.paid || null,
      isDesc: sortStateOrder === -1,
      bookingAdminSortId: sortState.field || 6,
    };
  };
  // Main function to get bookings data
  const getBookings = async (params, allowLoading = true) => {
    if (allowLoading) setLoading(true);
    try {
      const result = await getBookingsAdmin(params);
      if (result?.data?.headers?.error === 200) {
        setBookings(result?.data?.body?.items);
        setTotalCount(result?.data?.body?.totalItems);
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error(new Error(error)?.message);
    }
    if (allowLoading) setLoading(false);
  };

  const handleCompletedBooking = async (data) => {
    setLoading(true);
    const response = await bookingCompleted(data);
    if (response?.data?.headers?.error === 200) {
      toast.success("Booking Completed Successfully");
      setShowAreYouSureCompletedModal(false);
     await getBookings(getBookingParams());
    } else {
      toast.error(response?.data?.headers?.message);
    }
    setLoading(false);
  };
  // Use this explicit type for localSortMeta to match PrimeReact's requirements
  const [localSortMeta, setLocalSortMeta] = useState({
    sortField: 6, // BookingSortFields.BookingReference
    sortOrder: -1, // -1 for descending order
  });

  // Modified handleSort function to ensure proper DataTable interaction
  const handleSort = (event) => {
    console.log("Sort event:", event); // Log the sort event to debug
    // Update local state for immediate UI feedback
    setLocalSortMeta({
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    });

    // Dispatch to Redux for persistence
    dispatch(
      setSortState({
        field: event.sortField,
        order: event.sortOrder,
      })
    );

    // Get booking params with the updated sort parameters
    getBookings({
      ...getBookingParams(),
      isDesc: event.sortOrder === -1,
      bookingAdminSortId: event.sortField || 6,
    });
  };

  // Track page navigation to handle filter state
  useEffect(() => {
    // Mark that we're on the booking page
    dispatch(setLastVisitedPage("booking"));
    // Set page title and navigation
    dispatch(setPageTitle("Dashboard"));
    dispatch(setPageLink(""));

    // Initialize with default sort by Reference ID in descending order
    setSortState({
      field: 6, // BookingSortFields.BookingReference
      order: -1, // -1 for descending order
    });
    // setFieldValue("status", 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // When bookingFilter changes or component mounts, load data with sort state
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.allSettled([
        getBookings(getBookingParams(), false),
        getDashboardStatsApi({
          intervalId: 3,
        }),
      ]);
      setLoading(false);
    })();
  }, [
    values.status,
    values.paid,
    values.court,
    values.locations,
    values.selectDate,
    // Don't include sortState here to prevent double loading
  ]);

  // Define action items for the row actions
  const actionItems = [
    {
      label: "View Detail",
      icon: <ViewDetailIcon />,
      command: (rowData) => {
        navigate(`/bookings/${rowData?.bookingId}`, {
          state: { bookingData: rowData },
        });
      },
    },
    {
      label: "Receive Payment",
      icon: <ReceivePaymentIcon />,
      command: (rowData) => {
        setSelectedItem(rowData);
        setShowPaymentReceiveModal(true);
      },
    },
    {
      label: "Invoice",
      icon: <InvoiceIcon />,
      command: (rowData) => {
        window.open(
          `${process.env.REACT_APP_BASE_URL}/api/booking-admin/export-invoice/${
            rowData?.bookingId
          }?token=${localStorage.getItem("token")}`,
          "_blank"
        );
      },
    },
    {
      label: "Refund & Cancel",
      icon: <RefundCancelIcon />,
      command: (rowData) => {
        setSelectedItem(rowData);
        setShowRefundAndCancelModal(true);
      },
    },
  ];

  // Handle various booking actions
  const handleAdd = () => setShowAddOrEditBookingModal(true);
  const handleDeleteMultiple = () => console.log("Delete multiple");

  const handleAddAndRefund = (data) => {
    setSelectedItem(data);
    setShowRefundAndCancelModal(true);
  };

  const onReceivePayment = (data) => {
    setSelectedItem(data);
    setShowPaymentReceiveModal(true);
  };

  const onBookingCancel = (data) => {
    setSelectedItem(data);
    setShowCancelBookingModal(true);
  };

  const onBookingConfirm = (data) => {
    setSelectedItem(data);
    setShowPaymentConfirm(true);
  };

  // API functions for modal submissions
  const addBookingAdmin = async (data) => {
    setLoading(true);
    try {
      const result = await addBooking(data);
      if (result?.data?.headers?.error === 200) {
        toast.success("Booking added successfully");
        setShowAddOrEditBookingModal(false);
        getBookings(getBookingParams());
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error(new Error(error)?.message);
    }
    setLoading(false);
  };
  const cancelAndRefundApi = async (data) => {
    setLoading(true);
    try {
      const result = await refundAndCancel({
        ...data,
        cancelBooking: false,
        ...(refundCancelModalType !== "refund" && { cancelBooking: true }),
      });
      if (result?.data?.headers?.error === 200) {
        toast.success(
          `${
            refundCancelModalType !== "refund" ? "Cancelled" : "Refunded"
          } Successfully`
        );
        setShowRefundAndCancelModal(false);
        setShowCancelBookingModal(false);
        getBookings(getBookingParams());
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error("Failed to refund/cancel");
    }
    setLoading(false);
  };
  const paymentReceiveApi = async (data) => {
    setLoading(true);
    try {
      const result = await paymentReceive(data);
      if (result?.data?.headers?.error === 200) {
        toast.success(result?.data?.headers?.message);
        setShowPaymentReceiveModal(false);
        getBookings(getBookingParams());
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error("Failed to receive payment");
    }
    setLoading(false);
  };

  const confirmBookingApi = async (data) => {
    setLoading(true);
    try {
      const result = await confirmBooking({
        bookingId: data?.bookingId,
      });
      if (result?.data?.headers?.error === 200) {
        toast.success(result?.data?.headers?.message);
        setShowPaymentConfirm(false);
        getBookings(getBookingParams());
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error("Failed to confirm booking");
    }
    setLoading(false);
  };

    const onCompleted = (data) => {
    setSelectedItem(data);
    setShowAreYouSureCompletedModal(true);
  };
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <PaddingContainer>
            <section className={classes.titleContainer}>
              <div className={classes.filterActions}>
                <PageHeading label="Overall Summary" />
                <Button
                  leftIcon={<RefreshIcon />}
                  className={classes.refreshButton}
                  onClick={() => {
                    (async () => {
                      setLoading(true);
                      await Promise.allSettled([
                        getBookings(getBookingParams(), false),
                        getDashboardStatsApi({
                          intervalId: 1,
                        }),
                      ]);
                      setLoading(false);
                    })();
                  }}
                />
              </div>
              <Dropdown
                className={clsx(classes.filterAction, classes.bgWhite)}
                placeholder={"Export"}
                icon={<CalendarIcon />}
                options={statsPeriod}
                value={period}
                onChange={(e) => {
                  getDashboardStatsApi(
                    {
                      intervalId: e,
                    },
                    true
                  );
                  setPeriod(e);
                }}
              />
            </section>

            <Row className={classes.row}>
              <DashboardStats data={dashboardStats} period={period} />
              <Col sm={8} md={8}>
                <div className={classes.filterActions}>
                  <PageHeading label="Booking List" />
                  <div className={classes.tabsWrapper}>
                    <TabsContainer
                      tabs={
                        <>
                          {Object.keys(initialValues)
                            .slice(0, -1)
                            ?.map((filter) => (
                              <Dropdown
                                key={filter}
                                placeholder={formatText(filter)}
                                className={clsx(
                                  classes.filterAction,
                                  classes.bgWhite
                                )}
                                value={values[filter]}
                                onChange={handleChange(filter)}
                                options={options[filter]}
                              />
                            ))}
                          <div className={classes.datePickerContainer}>
                            <DatePicker
                              type="range"
                              value={values?.selectDate}
                              onChange={handleChange("selectDate")}
                            />
                            {values?.selectDate?.[0]?.startDate && (
                              <button
                                type="button"
                                className={classes.resetDateButton}
                                onClick={() => {
                                  setFieldValue("selectDate", [
                                    {
                                      startDate: null,
                                      endDate: null,
                                      key: "selection",
                                    },
                                  ]);
                                  dispatch(clearSelectDate());
                                }}
                              >
                                <RxCross2 className={classes.resetIcon} />
                              </button>
                            )}
                          </div>
                        </>
                      }
                    />
                  </div>
                </div>
                <DataTableWrapper
                  ref={dataTableRef}
                  search={search}
                  data={bookings}
                  columns={columns(
                    actionItems,
                    setSelectedItem,
                    navigate,
                    handleAddAndRefund,
                    onReceivePayment,
                    onBookingCancel,
                    onBookingConfirm,
                    setRefundCancelModalType,
                    onRescheduleBooking,
                    onCompleted
                  )}
                  onAdd={handleAdd}
                  onDeleteMultiple={handleDeleteMultiple}
                  loading={loading}
                  totalRecords={totalCount}
                  onPageChange={handlePageChange}
                  first={first}
                  rows={rows}
                  onSort={handleSort}
                  sortField={localSortMeta.sortField}
                  sortOrder={localSortMeta.sortOrder}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                />
              </Col>
              <Col sm={4} md={4}>
                <div className={classes.chartContainer}>
                  <PageHeading label="Revenue" className={classes.chartTitle} />
                  <Chart data={dashboardStats?.revenueGraph} />
                </div>
              </Col>
            </Row>
          </PaddingContainer>

          {/* Modals remain unchanged */}
          {showAddOrEditBookingModal && (
            <AddOrEditBookingModal
              isApiCall={loading}
              show={showAddOrEditBookingModal}
              setShow={setShowAddOrEditBookingModal}
              onSubmit={addBookingAdmin}
            />
          )}
          {showRefundAndCancelModal && (
            <AddAndRefundModal
              title={`${
                refundCancelModalType === "refund"
                  ? "Refund"
                  : "Refund And Cancel"
              } Booking`}
              type={refundCancelModalType}
              show={showRefundAndCancelModal}
              setShow={setShowRefundAndCancelModal}
              data={selectedItem}
              onSubmit={cancelAndRefundApi}
            />
          )}
          {showPaymentReceiveModal && (
            <PaymentReceivedModal
              show={showPaymentReceiveModal}
              setShow={setShowPaymentReceiveModal}
              data={selectedItem}
              onSubmit={paymentReceiveApi}
            />
          )}

          {showCancelBookingModal && selectedItem && (
            <AreYouSureModal
              show={showCancelBookingModal}
              setShow={setShowCancelBookingModal}
              data={selectedItem?.bookingNo?.split("-")?.slice(1).join("-")}
              onConfirm={() => {
                cancelAndRefundApi({
                  bookingId: selectedItem?.bookingId,
                  bookingStatusId: null,
                  refundAmount: 0,
                  reason: "Cancelled by Admin",
                  attachment: null,
                });
              }}
            />
          )}

          {showPaymentConfirm && (
            <PaymentReceivedModal
              label="Confirmation"
              show={showPaymentConfirm}
              setShow={setShowPaymentConfirm}
              data={selectedItem}
              onSubmit={confirmBookingApi}
            />
          )}
          {showRescheduleModal && (
            <RescheduleBookingModal
              subTitle={`Reference No: ${selectedItem?.bookingNo
                ?.split("-")
                ?.slice(1)
                .join("-")}`}
              show={showRescheduleModal}
              setShow={setShowRescheduleModal}
              data={selectedItem}
              onSubmit={rescheduleBookingApi}
            />
          )}
          {showAreYouSureCompletedModal && (
            <AreYouSureWantToCompleteModal
              loading={loading}
              show={showAreYouSureCompletedModal}
              data={selectedItem?.bookingNo?.split("-")?.slice(1).join("-")}
              setShow={setShowAreYouSureCompletedModal}
              onConfirm={() => {
                handleCompletedBooking({ bookingId: selectedItem?.bookingId });
              }}
            />
          )}
        </>
      )}
    </>
  );
}
